import { ChevronDownIcon } from 'lucide-react';
import { useCan } from '../useCan';
import { useCosts } from '../margin/useCosts';
import { MarginLines } from '../margin/MarginLines';
import { useAdmin } from '../AdminContext';
import { orderContribution, orderKindLabel, orderKindOf, staffelGiftCost } from '../../core/rules/margin';
import { refundVatByRate } from '../../core/rules/revenue';
import type { AdminOrder } from '../../core/types/adminRequests';

/**
 * Bestelling › Wat er overblijft. Ingeklapt; alleen met `finance.margin`. Geschat: inkoop is de
 * huidige gemiddelde prijs (de server legt hem bij verzenden per regel vast) en de kosten komen uit
 * Instellingen › Kosten en marge tot de echte Mollie- en PostNL-cijfers er zijn.
 */
export function OrderMarginPanel({
  order: o
}: {
  order: AdminOrder;
}) {
  const {
    canAll
  } = useCan();
  const allowed = canAll('finance.margin');
  const {
    data
  } = useCosts(allowed);
  const {
    site
  } = useAdmin();
  if (!allowed) return null;
  const kind = o.kind ?? orderKindOf(o.channel, o.lines);
  const counts = o.paymentStatus === 'betaald' && !o.cancelledAt;
  // Staffelcadeau: zelfde treden en artikelen als de server bij betalen afboekt. Bijvuldozen krijgen
  // geen staffel; bonnen (0% btw) tellen niet mee voor het bedrag.
  const staffelSubtotal = o.lines.filter(l => l.vatRate > 0).reduce((s, l) => s + l.lineTotalCents, 0) / 100;
  const giftCost = data && o.gift && kind !== 'bijvullen' ? staffelGiftCost(site.giftTiers, site.policy.freeShippingFrom, staffelSubtotal, data.itemCosts) : {
    cents: 0,
    unknown: 0
  };
  return <details className="group rounded-lg border border-line bg-white" data-id="element-3595" data-mp="42c8fpwt2yzk">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-4 sm:px-5 [&::-webkit-details-marker]:hidden" data-id="element-3596" data-mp="iz3daep912wk">
        <span className="min-w-0" data-id="element-3597" data-mp="1ucnpq5a3m4o4">
          <span className="block text-[15px] font-medium text-ink" data-id="element-3598" data-mp="1ubmdhxhcjc4t">Wat er overblijft</span>
          <span className="block text-[13px] text-muted" data-id="element-3599" data-mp="1pedu5tkgp21z">
            {orderKindLabel[kind]} · geschat · vóór vaste lasten
          </span>
        </span>
        <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted transition-transform duration-200 ease-out group-open:rotate-180" strokeWidth={1.75} aria-hidden="true" data-id="element-3600" data-mp="1wj53lxyq8j3g" />
      </summary>
      <div className="border-t border-line px-4 py-4 sm:px-5" data-id="element-3601" data-mp="26fqdawloyidm">
        {!counts ? <p className="text-[13px] text-muted" data-id="element-3602" data-mp="2bdjh5iziv0aq">Telt niet mee: deze bestelling is niet betaald of geannuleerd.</p> : !data ? <p className="text-[13px] text-muted" data-id="element-3603" data-mp="1ipdv8swqf59b">Laden…</p> : <>
            <MarginLines breakdown={orderContribution({
          kind,
          lines: o.lines.map(l => ({
            name: l.name,
            qty: l.qty,
            grossCents: l.lineTotalCents,
            vatRate: l.vatRate,
            costCents: data.itemCosts[l.productId] ?? null
          })),
          paymentMethod: kind === 'bijvullen' ? 'incasso' : o.paymentMethod,
          paidCents: Math.max(0, o.totalCents - o.voucherCents),
          shipping: o.address.country === 'BE' ? 'pakket-be' : 'pakket-nl',
          shippingChargedCents: o.shippingCents,
          shippingVatRate: o.shippingVatRate,
          giftCostCents: giftCost.cents,
          // Per tarief, met dezelfde verdeling als de creditnota (niet alles is 21%).
          refundedNetCents: o.refunds.reduce((n, r) => n + refundVatByRate(o, r).reduce((s, p) => s + p.grossCents - p.vatCents, 0), 0),
          // Ongeopend terug op voorraad: die inkoop is er weer.
          returnedCostCents: o.refunds.filter(r => r.restocked).flatMap(r => r.lines ?? []).reduce((n, l) => n + l.qty * (data.itemCosts[l.productId] ?? 0), 0)
        }, data.costs)} className="max-w-md" data-id="element-3604" data-mp="1in1doguvywws" />
            {o.gift && kind !== 'bijvullen' && <p className="mt-3 text-[12px] text-muted" data-id="element-3605" data-mp="26n21xbqgxdtr">
                Cadeau ({o.gift}) telt mee tegen de inkoopprijs
                {giftCost.unknown > 0 ? '; een cadeau zonder gekoppeld artikel of inkoopprijs telt als € 0' : ''}.
              </p>}
          </>}
      </div>
    </details>;
}